// fundAdmin.ts — pure state/validation logic for "Fonds hinzufügen" (Daten view). Kept
// framework-free (no Preact) so the lookup -> confirm -> add transitions and the
// currency/OeKB-fallback validation rules are unit-testable without rendering anything —
// vitest runs this package in a Node environment with no DOM (see vite.config.ts), so a
// component test would need a heavier setup this repo deliberately doesn't carry.
//
// GUARDRAIL (see task brief): never fabricate fund metadata or a ticker. OeKB is
// authoritative whenever it has an entry; the confirm form's fields are only a fallback
// for a genuine "no entry", and a non-EUR/unresolvable ticker must block submission
// (D-013 — this app never FX-converts prices).
import { ApiError } from './api.js';
import type { FundLookupResponse } from './types.js';
import type { CreateFundBody } from './types.js';

export const ISIN_FORMAT_RE = /^[A-Z]{2}[A-Z0-9]{10}$/;

export function normalizeIsin(raw: string): string {
  return raw.trim().toUpperCase();
}

export function isValidIsinFormat(raw: string): boolean {
  return ISIN_FORMAT_RE.test(normalizeIsin(raw));
}

/** The confirm-step form fields — one flat, controlled-input-friendly shape covering both
 * the Yahoo-ticker pick (candidate or manual) and the OeKB-fallback fields. */
export interface ConfirmForm {
  /** Currently selected candidate symbol, '' if none picked yet. */
  selectedSymbol: string;
  /** Hand-typed symbol, used when useManual is true (search returned nothing, or the user
   * opted out of every candidate). */
  manualSymbol: string;
  useManual: boolean;
  /** Fallback fields — only actually used (and only required) when lookup.oekb is null. */
  name: string;
  currency: string;
  kind: 'acc' | 'dist' | '';
  meldefonds: boolean;
  /** Raw text input for TER (basis points), optional; '' = omitted. */
  terBp: string;
}

/** Seeds the confirm form from a lookup response: pre-selects the first Yahoo candidate
 * (or falls straight to manual entry if search found none) and pre-fills the OeKB fields
 * when OeKB had a match (informational — only actually sent to the server when OeKB had
 * no entry at all, see buildCreateFundBody). */
export function initialConfirmForm(lookup: FundLookupResponse): ConfirmForm {
  const first = lookup.yahooCandidates[0];
  return {
    selectedSymbol: first?.symbol ?? '',
    manualSymbol: '',
    useManual: lookup.yahooCandidates.length === 0,
    name: lookup.oekb?.name ?? '',
    currency: lookup.oekb?.currency ?? '',
    kind: lookup.oekb?.kind ?? '',
    meldefonds: lookup.oekb?.meldefonds ?? false,
    terBp: '',
  };
}

/** The ticker that would actually be submitted right now. */
export function effectiveYahooSymbol(form: ConfirmForm): string {
  return (form.useManual ? form.manualSymbol : form.selectedSymbol).trim();
}

function selectedCandidate(lookup: FundLookupResponse, form: ConfirmForm) {
  return lookup.yahooCandidates.find((c) => c.symbol === form.selectedSymbol);
}

/** Human-readable currency warning for the currently selected candidate, or null when
 * there's nothing to warn about (EUR, or currency not yet known — manual entry / a
 * candidate whose live currency check failed). Never guesses a currency. */
export function currencyWarning(lookup: FundLookupResponse, form: ConfirmForm): string | null {
  if (form.useManual) return null;
  const candidate = selectedCandidate(lookup, form);
  if (!candidate || candidate.currency === null || candidate.currency === 'EUR') return null;
  return (
    `${candidate.symbol} notiert in ${candidate.currency}, nicht EUR. ` +
    `Kurse werden nicht automatisch umgerechnet — dieser Fonds kann nicht hinzugefügt werden.`
  );
}

/** True when OeKB has no entry and the confirm form must supply name/currency/kind itself
 * before the "Hinzufügen" button can be enabled. */
export function needsManualFundFields(lookup: FundLookupResponse): boolean {
  return lookup.oekb === null;
}

/** Gates the "Hinzufügen" button: a real ticker is picked (and, if its currency is already
 * known, it must be EUR), and — only when OeKB has nothing — the fallback fields are
 * filled in. Does not itself hit the network; the server re-verifies everything
 * (currency, OeKB) independently before actually inserting. */
export function canSubmit(lookup: FundLookupResponse, form: ConfirmForm): boolean {
  if (!effectiveYahooSymbol(form)) return false;
  if (currencyWarning(lookup, form)) return false;
  if (needsManualFundFields(lookup)) {
    if (!form.name.trim() || !form.currency.trim() || !form.kind) return false;
  }
  return true;
}

/** Builds the POST /api/funds body. Fallback fields are included only when OeKB has no
 * entry — when OeKB matched, the server is the one deciding name/currency/kind, so
 * sending stale form values (pre-filled for display only) would be misleading. */
export function buildCreateFundBody(isin: string, lookup: FundLookupResponse, form: ConfirmForm): CreateFundBody {
  const body: CreateFundBody = {
    isin: normalizeIsin(isin),
    yahooSymbol: effectiveYahooSymbol(form),
  };
  if (needsManualFundFields(lookup)) {
    body.name = form.name.trim();
    body.currency = form.currency.trim().toUpperCase();
    if (form.kind) body.kind = form.kind;
    body.meldefonds = form.meldefonds;
  }
  const trimmedTer = form.terBp.trim();
  if (trimmedTer !== '') {
    const n = Number(trimmedTer);
    if (Number.isFinite(n)) body.terBp = Math.round(n);
  }
  return body;
}

/** Same error-message convention used across the app (DatenView's refresh handler, etc.):
 * the server's own honest reason (ApiError.message already carries it) when we have one,
 * a generic fallback for a genuine network/offline failure otherwise. Used for every
 * failure surfaced by this flow (lookup, add, delete) — including 409/422 from POST
 * /api/funds, which arrive as a normal ApiError with the server's message intact. */
export function describeApiError(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Unbekannter Fehler.';
}
